import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../UIElements/Icon";
import { convertTime, useGlobalUtils } from "../../../lib/globalUtils";

const AiPerformanceCards = ({ data, isLoading, error }) => {
  const { t } = useTranslation("overview");
  const { parsePrice } = useGlobalUtils();

  // Helper function to format change percentage
  const formatChangePercentage = (change) => {
    if (change === null || change === undefined) return null;
    const isPositive = change > 0;
    const isZero = change === 0;
    return {
      value: `${isPositive ? "+" : ""}${change.toFixed(1)}%`,
      isPositive,
      isZero,
    };
  };

  // Helper function to format hours using globalUtils
  const formatHours = (minutes) => {
    return convertTime(minutes, "min", "min", {
      decimals: 1,
      includeUnit: true,
    });
  };

  // Helper function to format ROI
  const formatROI = (roi) => {
    return `${roi.toFixed(2)}x`;
  };

  // Helper function to format cost using globalUtils
  const formatCost = (cost) => {
    return parsePrice(cost, { decimal: 2 });
  };

  // Helper function to format response time using globalUtils
  const formatResponseTime = (ms) => {
    return convertTime(ms, "ms", "s", { decimals: 1, includeUnit: true });
  };

  if (isLoading) {
    return (
      <div className="border-border bg-card text-card-foreground rounded-lg border p-4 sm:p-6">
        <div className="animate-pulse motion-reduce:animate-none">
          <div className="mb-4 h-5 w-1/3 rounded bg-gray-700 sm:mb-6 sm:h-6"></div>
          <div className="space-y-3 sm:space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-muted rounded-md p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="bg-accent h-8 w-8 rounded-md sm:h-10 sm:w-10"></div>
                    <div className="space-y-1">
                      <div className="bg-accent h-3 w-20 rounded sm:h-4 sm:w-24"></div>
                      <div className="bg-accent h-2 w-24 rounded sm:h-3 sm:w-32"></div>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <div className="bg-accent h-6 w-12 rounded sm:h-8 sm:w-16"></div>
                    <div className="bg-accent h-2 w-8 rounded sm:h-3 sm:w-12"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border-error/30 bg-card text-card-foreground rounded-lg border p-4 sm:p-6">
        <div className="text-center">
          <div className="bg-error/10 mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full sm:mb-4 sm:h-16 sm:w-16">
            <Icon
              name="AlertTriangle"
              className="text-error h-6 w-6 sm:h-8 sm:w-8"
            />
          </div>
          <h3 className="text-error mb-2 text-base font-medium sm:text-lg">
            {t("performance.error.title")}
          </h3>
          <p className="text-muted-foreground text-sm">
            {error.message || t("performance.error.subtitle")}
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="border-border bg-card text-card-foreground rounded-lg border p-4 sm:p-6">
        <div className="text-center">
          <div className="bg-muted mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full sm:mb-4 sm:h-16 sm:w-16">
            <Icon
              name="BarChart3"
              className="text-muted-foreground h-6 w-6 sm:h-8 sm:w-8"
            />
          </div>
          <h3 className="mb-2 text-base font-medium sm:text-lg">
            {t("performance.empty.title")}
          </h3>
          <p className="text-muted-foreground text-sm">
            {t("performance.empty.subtitle")}
          </p>
        </div>
      </div>
    );
  }

  const performanceCards = [
    data.conversion_rate_percentage?.hidden !== true && {
      id: "conversion-rate",
      title: t("performance.cards.conversionRate.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: `${data.conversion_rate_percentage?.previous?.toFixed(0) ?? 0}%`,
      }),
      value: `${data.conversion_rate_percentage?.current?.toFixed(0) ?? 0}%`,
      unit: t("performance.cards.conversionRate.unit"),
      icon: "BarChart3",
      iconBg: "bg-primary/10",
      iconColor: "text-primary",
      valueColor: "text-foreground",
      change: formatChangePercentage(
        data.conversion_rate_percentage?.change_percentage,
      ),
    },
    data.human_minutes_saved?.hidden !== true && {
      id: "hours-saved",
      title: t("performance.cards.hoursSaved.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: formatHours(data.human_minutes_saved?.previous ?? 0),
      }),
      value: formatHours(data.human_minutes_saved?.current ?? 0),
      unit: t("performance.cards.hoursSaved.unit"),
      icon: "Bot",
      iconBg: "bg-primary/10",
      iconColor: "text-primary",
      valueColor: "text-foreground",
      change: formatChangePercentage(
        data.human_minutes_saved?.change_percentage,
      ),
    },
    data.average_response_time_ms?.hidden !== true && {
      id: "response-time",
      title: t("performance.cards.responseTime.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: formatResponseTime(data.average_response_time_ms?.previous ?? 0),
      }),
      value: formatResponseTime(data.average_response_time_ms?.current ?? 0),
      unit: t("performance.cards.responseTime.unit"),
      icon: "Clock",
      iconBg: "bg-warning/10",
      iconColor: "text-warning",
      valueColor: "text-foreground",
      change: formatChangePercentage(
        data.average_response_time_ms?.change_percentage,
      ),
      changeColor:
        data.average_response_time_ms?.change_percentage === 0
          ? "text-warning"
          : data.average_response_time_ms?.change_percentage > 0
            ? "text-error"
            : "text-success",
    },
    data.average_cost_per_conversation?.hidden !== true && {
      id: "cost-per-conversation",
      title: t("performance.cards.costPerConversation.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: formatCost(data.average_cost_per_conversation?.previous ?? 0),
      }),
      value: formatCost(data.average_cost_per_conversation?.current ?? 0),
      unit: t("performance.cards.costPerConversation.unit"),
      icon: "DollarSign",
      iconBg: "bg-success/10",
      iconColor: "text-success",
      valueColor: "text-foreground",
      change: formatChangePercentage(
        data.average_cost_per_conversation?.change_percentage,
      ),
      changeColor:
        data.average_cost_per_conversation?.change_percentage === 0
          ? "text-warning"
          : data.average_cost_per_conversation?.change_percentage > 0
            ? "text-error"
            : "text-success",
    },
    data.roi_orders_per_dollar?.hidden !== true && {
      id: "roi-orders",
      title: t("performance.cards.roi.title"),
      subtitle: t("performance.yesterdayPrefix", {
        value: formatROI(data.roi_orders_per_dollar?.previous ?? 0),
      }),
      value: formatROI(data.roi_orders_per_dollar?.current ?? 0),
      unit: t("performance.cards.roi.unit"),
      icon: "TrendingUp",
      iconBg: "bg-primary/10",
      iconColor: "text-primary",
      valueColor: "text-primary",
      change: formatChangePercentage(
        data.roi_orders_per_dollar?.change_percentage,
      ),
      gradient: true,
    },
  ].filter(Boolean);

  return (
    <div className="border-border bg-card text-card-foreground rounded-lg border p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between sm:mb-6">
        <div>
          <h3 className="text-base font-semibold sm:text-lg">
            {t("performance.title")}
          </h3>
          <p className="text-muted-foreground text-xs sm:text-sm">
            {t("performance.subtitle")}
          </p>
        </div>
      </div>

      <div className="space-y-3 sm:space-y-4">
        {performanceCards.map((card) => (
          <div
            key={card.id}
            className={`border-border bg-muted flex items-center justify-between rounded-md border p-3 transition-colors duration-200 motion-reduce:transition-none sm:p-4 ${
              card.gradient ? "border-primary/30 bg-primary/10" : ""
            }`}
          >
            {/* Left side - Icon and Info */}
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              <div
                className={`h-8 w-8 sm:h-10 sm:w-10 ${card.iconBg} flex flex-shrink-0 items-center justify-center rounded-md`}
              >
                <Icon
                  name={card.icon}
                  className={`h-4 w-4 sm:h-5 sm:w-5 ${card.iconColor}`}
                />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="text-foreground truncate text-xs font-medium sm:text-sm">
                  {card.title}
                </h4>
                <p className="text-muted-foreground truncate text-xs">
                  {card.subtitle}
                </p>
              </div>
            </div>

            {/* Right side - Values */}
            <div className="ms-2 flex-shrink-0 text-end">
              <div className="flex flex-col items-end">
                <div className="flex items-baseline gap-1">
                  <span
                    className={`text-lg font-bold sm:text-2xl ${card.valueColor}`}
                  >
                    {card.value}
                  </span>
                  <span className="text-muted-foreground hidden text-xs sm:inline sm:text-sm">
                    {card.unit}
                  </span>
                </div>

                {card.change && (
                  <div className="mt-0.5 flex items-center gap-1">
                    <span
                      className={`text-xs font-medium ${
                        card.changeColor ??
                        (card.change.isZero
                          ? "text-warning"
                          : card.change.isPositive
                            ? "text-success"
                            : "text-error")
                      }`}
                    >
                      {card.change.value}
                    </span>
                    <span className="text-muted-foreground hidden text-xs sm:inline">
                      {t("performance.comparison")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AiPerformanceCards;
